import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const PAGE_SIZE = 48;

/**
 * Pages for a product listing.
 *
 * There were none. `Shop.jsx`, `Category.jsx` and `Collections.jsx` each asked
 * for `page: 1, page_size: 48` and rendered whatever came back — no numbers, no
 * "load more", no infinite scroll. `/shop` announced 260 products and showed
 * 48, so **212 of them could not be reached by any means**: not by clicking,
 * not by scrolling, not by editing the URL. Four fifths of the catalogue was
 * simply not for sale.
 *
 * URL-driven rather than a "load more" button, because the page number then
 * behaves like the colour filter already does: shareable, restored on a deep
 * link, and reachable by the back button. It also gives a crawler a path
 * through the whole catalogue, which `rel="next"`/`rel="prev"` reinforces.
 */
export default function Pagination({ page, total, pageSize = PAGE_SIZE, onChange, testId = "pagination" }) {
  const { locale } = useI18n();
  const pages = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
  const current = Math.min(Math.max(1, Number(page) || 1), pages);

  // Crawlers follow these even though the SPA renders client-side, and they
  // stop a paginated set being read as duplicate content.
  useEffect(() => {
    const set = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"][data-listing="true"]`);
      if (!href) { el?.remove(); return; }
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-listing", "true");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    const url = (n) => {
      const next = new URL(window.location.href);
      if (n <= 1) next.searchParams.delete("page");
      else next.searchParams.set("page", String(n));
      return next.toString();
    };
    set("prev", current > 1 ? url(current - 1) : null);
    set("next", current < pages ? url(current + 1) : null);
    return () => { set("prev", null); set("next", null); };
  }, [current, pages]);

  if (pages <= 1) return null;

  const copy = locale === "az"
    ? { prev: "Əvvəlki", next: "Növbəti", page: "Səhifə", of: "/", nav: "Səhifələr" }
    : { prev: "Previous", next: "Next", page: "Page", of: "of", nav: "Pagination" };

  // First, last, and a window around the current page. Anything else becomes an
  // ellipsis, so 40 pages do not produce 40 buttons on a phone.
  const window_ = new Set([1, pages, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => window_.add(n));
  if (current >= pages - 2) [pages - 3, pages - 2, pages - 1].forEach((n) => window_.add(n));
  const shown = [...window_].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);

  const items = [];
  shown.forEach((n, i) => {
    if (i > 0 && n - shown[i - 1] > 1) items.push({ gap: true, key: `gap-${n}` });
    items.push({ n, key: `p-${n}` });
  });

  const go = (n) => {
    if (n < 1 || n > pages || n === current) return;
    onChange(n);
  };

  return (
    <nav className="adf-pagination" aria-label={copy.nav} data-testid={testId}>
      <button
        type="button"
        onClick={() => go(current - 1)}
        disabled={current === 1}
        aria-label={copy.prev}
        className="adf-pagination__step"
        data-testid={`${testId}-prev`}
      >
        <ChevronLeft size={17} strokeWidth={1.5} aria-hidden="true" />
      </button>

      <ol className="adf-pagination__pages">
        {items.map((item) => (
          <li key={item.key}>
            {item.gap ? (
              <span className="adf-pagination__gap" aria-hidden="true">…</span>
            ) : (
              <button
                type="button"
                onClick={() => go(item.n)}
                aria-current={item.n === current ? "page" : undefined}
                aria-label={`${copy.page} ${item.n}`}
                className={`adf-pagination__page${item.n === current ? " is-current" : ""}`}
                data-testid={`${testId}-page-${item.n}`}
              >
                {item.n}
              </button>
            )}
          </li>
        ))}
      </ol>

      <button
        type="button"
        onClick={() => go(current + 1)}
        disabled={current === pages}
        aria-label={copy.next}
        className="adf-pagination__step"
        data-testid={`${testId}-next`}
      >
        <ChevronRight size={17} strokeWidth={1.5} aria-hidden="true" />
      </button>

      <p className="adf-pagination__status" aria-live="polite">
        {copy.page} {current} {copy.of} {pages}
      </p>
    </nav>
  );
}

export { PAGE_SIZE };
